import {
    BraintreeError,
    BraintreeHostWindow,
    BraintreeInitializationData,
    BraintreePaypalSdkCreatorConfig,
    BraintreePaypalWalletService,
    isBraintreeError,
    PaypalAuthorizeData,
    PaypalButtonStyleColorOption,
} from '@bigcommerce/checkout-sdk/braintree-utils';
import {
    CheckoutButtonInitializeOptions,
    CheckoutButtonStrategy,
    InvalidArgumentError,
    MissingDataError,
    MissingDataErrorType,
    PaymentMethod,
    StandardError,
} from '@bigcommerce/checkout-sdk/payment-integration-api';

import getValidButtonStyle from '../get-valid-button-style';

import BraintreePaypalWalletInitializeOptions, {
    WithBraintreePaypalWalletInitializeOptions,
} from './braintree-paypal-wallet-initialize-options';

export default class BraintreePaypalWalletStrategy implements CheckoutButtonStrategy {
    constructor(
        private braintreePaypalWalletService: BraintreePaypalWalletService,
        private braintreeHostWindow: BraintreeHostWindow,
    ) {}

    async initialize(
        options: CheckoutButtonInitializeOptions & WithBraintreePaypalWalletInitializeOptions,
    ): Promise<void> {
        const { braintreepaypal, containerId, methodId } = options;

        if (!methodId) {
            throw new InvalidArgumentError(
                'Unable to initialize payment because "options.methodId" argument is not provided.',
            );
        }

        if (!containerId) {
            throw new InvalidArgumentError(
                `Unable to initialize payment because "options.containerId" argument is not provided.`,
            );
        }

        if (!braintreepaypal) {
            throw new InvalidArgumentError(
                `Unable to initialize payment because "options.braintreepaypal" argument is not provided.`,
            );
        }

        let parsedPaymentMethod: PaymentMethod<BraintreeInitializationData>;

        try {
            parsedPaymentMethod = JSON.parse(atob(braintreepaypal.initializationData));
        } catch (error) {
            throw new InvalidArgumentError("Failed to parse payment method 'initializationData'.");
        }

        const { initializationData, config } = parsedPaymentMethod;

        if (!braintreepaypal.clientToken || !initializationData || !config) {
            throw new MissingDataError(MissingDataErrorType.MissingPaymentMethod);
        }

        const paypalCheckoutOptions: BraintreePaypalSdkCreatorConfig = {
            currency: braintreepaypal.currency.code,
            intent: initializationData.intent,
            isCreditEnabled: initializationData.isCreditEnabled,
            commit: false,
        };

        this.braintreePaypalWalletService.initialize(braintreepaypal.clientToken);

        await this.braintreePaypalWalletService.loadPaypalCheckout(
            paypalCheckoutOptions,
            containerId,
            braintreepaypal.onError,
        );

        this.renderButton(
            braintreepaypal,
            containerId,
            methodId,
            !!config.testMode,
            initializationData.intent,
        );
    }

    async deinitialize(): Promise<void> {
        await this.braintreePaypalWalletService.teardown();
    }

    private renderButton(
        braintreepaypal: BraintreePaypalWalletInitializeOptions,
        containerId: string,
        methodId: string,
        testMode: boolean,
        intent?: BraintreeInitializationData['intent'],
    ): void {
        const { style, onEligibilityFailure } = braintreepaypal;
        const { paypal } = this.braintreeHostWindow;

        if (!paypal) {
            this.braintreePaypalWalletService.removeElement(containerId);

            return;
        }

        const fundingSources = [paypal.FUNDING.PAYPAL, paypal.FUNDING.PAYLATER];

        let hasRenderedButton = false;

        fundingSources.forEach((fundingSource) => {
            const buttonStyle =
                fundingSource === paypal.FUNDING.PAYLATER
                    ? { ...getValidButtonStyle(style), color: PaypalButtonStyleColorOption.GOLD }
                    : getValidButtonStyle(style);

            const paypalButtonRender = paypal.Buttons({
                env: testMode ? 'sandbox' : 'production',
                fundingSource,
                style: buttonStyle,
                createOrder: () => this.setupPayment(braintreepaypal, intent),
                onApprove: (authorizeData: PaypalAuthorizeData) =>
                    this.tokenizePayment(
                        authorizeData,
                        methodId,
                        braintreepaypal.cartId,
                        braintreepaypal.onAuthorizeError,
                    ),
            });

            if (paypalButtonRender.isEligible()) {
                paypalButtonRender.render(`#${containerId}`);
                hasRenderedButton = true;
            }
        });

        if (
            !hasRenderedButton &&
            onEligibilityFailure &&
            typeof onEligibilityFailure === 'function'
        ) {
            onEligibilityFailure();
        }
    }

    private async setupPayment(
        braintreepaypal: BraintreePaypalWalletInitializeOptions,
        intent?: BraintreeInitializationData['intent'],
    ): Promise<string | void> {
        const { onPaymentError, amount, currency } = braintreepaypal;

        const braintreePaypalCheckout =
            this.braintreePaypalWalletService.getBraintreePaypalCheckoutOrThrow();

        try {
            return await braintreePaypalCheckout.createPayment({
                flow: 'checkout',
                enableShippingAddress: true,
                shippingAddressEditable: false,
                amount,
                currency: currency.code,
                offerCredit: false,
                intent,
            });
        } catch (error: unknown) {
            if (isBraintreeError(error) || error instanceof StandardError) {
                onPaymentError?.(error);
            }

            throw error;
        }
    }

    private async tokenizePayment(
        authorizeData: PaypalAuthorizeData,
        methodId: string,
        cartId: string,
        onError?: (error: BraintreeError | StandardError) => void,
    ): Promise<void> {
        try {
            await this.braintreePaypalWalletService.proxyTokenizationPayment(
                authorizeData,
                methodId,
                cartId,
            );
        } catch (error) {
            if (isBraintreeError(error) || error instanceof StandardError) {
                onError?.(error);
            }

            throw error;
        }
    }
}
